import React from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { ScoreBand } from '../../../store/callStats/callStatsTypes';
import {
  borderGrey,
  darkPurple,
  inactive,
  lightGrey,
} from '../../../mainTheme';
import { bandColor, bandLabel } from './scoreColors';

interface Props {
  label: string;
  /** numeric score used for the ring fill (0..max) */
  score: number;
  max?: number;
  band: ScoreBand;
  /** what to print in the center (defaults to the rounded score) */
  display?: string;
  highlight?: boolean;
  /** no calls scored in the window — render neutral "—", not a red "Poor". */
  unscored?: boolean;
}

const SIZE = 96;
const STROKE = 9;
const R = (SIZE - STROKE) / 2;
const C = 2 * Math.PI * R;

const ScoreCard: React.FC<Props> = ({
  label,
  score,
  max = 100,
  band,
  display,
  highlight,
  unscored,
}) => {
  const pct = unscored ? 0 : Math.max(0, Math.min(1, max ? score / max : 0));
  const color = unscored ? inactive : bandColor[band];
  return (
    <Paper
      elevation={0}
      sx={{
        border: highlight ? `2px solid ${color}` : `1px solid ${borderGrey}`,
        borderRadius: '16px',
        p: 2,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1,
      }}
    >
      <Typography
        variant="caption"
        sx={{ color: lightGrey, textAlign: 'center' }}
      >
        {label}
      </Typography>
      <Box position="relative" width={SIZE} height={SIZE}>
        <svg width={SIZE} height={SIZE}>
          <circle
            cx={SIZE / 2}
            cy={SIZE / 2}
            r={R}
            fill="none"
            stroke={inactive}
            strokeWidth={STROKE}
          />
          <circle
            cx={SIZE / 2}
            cy={SIZE / 2}
            r={R}
            fill="none"
            stroke={color}
            strokeWidth={STROKE}
            strokeLinecap="round"
            strokeDasharray={`${C * pct} ${C}`}
            transform={`rotate(-90 ${SIZE / 2} ${SIZE / 2})`}
          />
        </svg>
        <Box
          position="absolute"
          top={0}
          left={0}
          width={SIZE}
          height={SIZE}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Typography
            sx={{
              fontSize: 22,
              fontWeight: 700,
              color: unscored ? lightGrey : darkPurple,
            }}
          >
            {unscored ? '—' : (display ?? Math.round(score))}
          </Typography>
        </Box>
      </Box>
      <Typography
        variant="caption"
        sx={{ color: unscored ? lightGrey : color, fontWeight: 600 }}
      >
        {unscored ? 'No data' : bandLabel[band]}
      </Typography>
    </Paper>
  );
};

export default ScoreCard;
